import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

/**
 * TopSupportersModal — reusable overlay listing top supporters of a
 * track (kind="track") OR an artist (kind="artist"). Lives at the top of
 * the DOM as a fixed overlay, never as a child of the AudioPlayer's tree,
 * so opening it never interrupts playback.
 *
 * Props:
 *   open        : boolean
 *   onClose     : () => void
 *   kind        : "track" | "artist"
 *   targetId    : submission_id | user_id
 *   title       : optional string for the header
 *   refreshKey  : bumping this number forces a refetch (for real-time
 *                 updates after a gift is sent)
 */
const BadgeChip = ({ badge }) => (
  <span
    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] font-semibold text-white/90"
    title={badge.label}
    data-testid={`supporter-badge-${badge.id}`}
  >
    <span>{badge.emoji}</span>
    <span>{badge.label}</span>
  </span>
);

const RankMedal = ({ rank }) => {
  if (rank === 1) return <span className="text-2xl">🥇</span>;
  if (rank === 2) return <span className="text-2xl">🥈</span>;
  if (rank === 3) return <span className="text-2xl">🥉</span>;
  return <span className="text-pink-200/70 text-sm font-bold w-6 text-center">#{rank}</span>;
};

const TopSupportersModal = ({ open, onClose, kind, targetId, title, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !targetId) return;
    let cancelled = false;
    setLoading(true);
    const url = kind === "track"
      ? `/top-gifters/track/${targetId}?limit=25`
      : `/top-gifters/artist/${targetId}?limit=25`;
    api.get(url)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ supporters: [], count: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, kind, targetId, refreshKey]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const supporters = data?.supporters || [];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-label="Top Supporters"
      data-testid="top-supporters-modal"
    >
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        onClick={onClose}
        data-testid="top-supporters-backdrop"
      />
      <div
        className="relative w-full sm:max-w-lg max-h-[88vh] sm:max-h-[80vh] overflow-y-auto bg-gradient-to-b from-[#1a0d2e] via-[#0f0a1f] to-[#0a0a14] border-t sm:border border-amber-500/30 sm:rounded-2xl shadow-2xl shadow-amber-500/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-b from-[#1a0d2e] to-[#160c2599] backdrop-blur-md flex items-center justify-between px-4 py-3 border-b border-amber-500/20">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 font-bold">
              🏆 Top Supporters
            </p>
            <p className="text-sm text-white font-semibold truncate">
              {title || (kind === "track" ? "This track" : "This artist")}
              {data?.supporter_count != null && (
                <span className="text-pink-200/60 font-normal ml-2">
                  · {data.supporter_count} supporter{data.supporter_count === 1 ? "" : "s"}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-pink-200/70 hover:text-white text-2xl leading-none px-2"
            aria-label="Close"
            data-testid="top-supporters-close"
          >
            ×
          </button>
        </div>

        {/* List */}
        <div className="px-3 sm:px-4 py-3 space-y-2">
          {loading && supporters.length === 0 && (
            <p className="text-pink-200/60 text-sm text-center py-8" data-testid="top-supporters-loading">
              Loading supporters…
            </p>
          )}
          {!loading && supporters.length === 0 && (
            <div
              className="text-center py-10 text-pink-200/70 text-sm"
              data-testid="top-supporters-empty"
            >
              <p className="text-3xl mb-2">🎁</p>
              <p>No supporters yet — be the first to gift.</p>
            </div>
          )}
          {supporters.map((s) => (
            <Link
              key={s.user_id}
              to={`/artist/${s.user_id}`}
              onClick={onClose}
              className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-500/40 transition-colors"
              data-testid={`top-supporter-row-${s.user_id}`}
            >
              <RankMedal rank={s.rank} />
              <img
                src={getProfilePhotoUrl(s)}
                alt={s.artist_name}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-amber-400/40"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120&h=120&fit=crop"; }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{s.artist_name}</p>
                <p className="text-xs text-amber-200/80">
                  {s.total_coins.toLocaleString()} coins · {s.gift_count} gift{s.gift_count === 1 ? "" : "s"}
                </p>
                {s.top_gift && s.top_gift.coins > 0 && (
                  <p className="text-[11px] text-pink-200/70 mt-0.5">
                    Top: {s.top_gift.emoji} {s.top_gift.name} ({s.top_gift.coins.toLocaleString()}c)
                  </p>
                )}
                {Array.isArray(s.supporter_badges) && s.supporter_badges.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {s.supporter_badges.map((b) => (
                      <BadgeChip key={b.id} badge={b} />
                    ))}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopSupportersModal;
